import { DirectionalHint, Icon, type ITooltipHostStyles, TooltipHost } from "@fluentui/react";
import { type TFunction } from "i18next";
import type LoadCaseResultDto from "../../../../dto/calculations/LoadCaseResultDto";
import { HeadersTooltips } from "./headersTooltips";
import styles from "../sidePanel/constructionCalculationsPanel.module.scss";
import type LoadCaseResultValueDto from "../../../../dto/calculations/LoadCaseResultValueDto";
import type MastValidationControlDto from "../../../../dto/calculations/MastValidationControlDto";
import type FoundationValidationControlDto from "../../../../dto/calculations/FoundationValidationControlDto";
import {v4 as uuidv4} from "uuid";

export const renderHeaders = (loadCaseResult: LoadCaseResultDto[], t: TFunction<[string], undefined>): React.ReactNode[] => {
  const headers: React.ReactNode[] = [];

  for (let i = 0; i < loadCaseResult[0].loadCaseResultValues.length; i++) {
    headers.push(
      <TooltipHost
        content={t(loadCaseResult[0].loadCaseResultValues[i].abbreviation)}>
        {loadCaseResult[0].loadCaseResultValues[i].abbreviation}
      </TooltipHost>,
    );
  }

  for (let i = 0; i < loadCaseResult[0].mastValidationControls.length; i++) {
    if (loadCaseResult[0].mastValidationControls[i].isVisible) {
      headers.push(loadCaseResult[0].mastValidationControls[i].abbreviation);
    }
  }

  if (loadCaseResult[0].foundationValidationControls?.isFoundationValid !== null) {
    headers.push("Foundation OK");
  }

  if (loadCaseResult[0].footplateValidationControls !== null) {
    headers.push("Footplate OK");
  }

  // eslint-disable-next-line react/jsx-key
  const results = [<th key="def" />];

  const hostStyles: Partial<ITooltipHostStyles> = {root: {display: "grid"}};
  const calloutProps = {gapSpace: 0};

  for (let i = 0; i < headers.length; i++) {
    if (
      (headers[i] === "DBL" ||
        headers[i] === "DML" ||
        headers[i] === "BT" ||
        headers[i] === "zt" ||
        headers[i] === "ft") &&
      HeadersTooltips[headers[i] as string]
    ) {
      results.push(
        <th key={i} style={{cursor: "pointer"}}>
          <TooltipHost
            content={HeadersTooltips[headers[i] as string]}
            id={styles.tooltipRes}
            calloutProps={calloutProps}
            styles={hostStyles}
            directionalHint={DirectionalHint.bottomCenter}>
            <div>{headers[i]}</div>
          </TooltipHost>
        </th>,
      );
    } else {
      results.push(
        <th key={i} style={{cursor: "pointer"}}>
          {headers[i]}
        </th>,
      );
    }
  }

  return results;
};

export const renderResults = (loadCaseResult: LoadCaseResultDto[], t: TFunction<[string], undefined>): React.ReactNode[] => {
  const result: React.ReactNode[] = [];

  const hostStyles: Partial<ITooltipHostStyles> = {root: {display: "grid"}};
  const calloutProps = {gapSpace: 0};

  for (let i = 0; i < loadCaseResult.length; i++) {
    result.push(
      <tr key={i} className={styles.result}>
        <td>
          <TooltipHost
            content={
              <div>
                <p>
                  {`Additional information for ${loadCaseResult[i].loadCase.slsUls}${loadCaseResult[i].loadCase.wind.type} `}
                  <Icon
                    iconName={getIconByWindDirection(
                      loadCaseResult[i].loadCase.wind.direction,
                    )}
                  />
                  {` to show here.`}
                </p>
              </div>
            }
            id={styles.tooltip}
            calloutProps={calloutProps}
            styles={hostStyles}
            directionalHint={DirectionalHint.rightCenter}>
            <div className={styles.loadCaseName}>
              <p>
                {t(
                  `${loadCaseResult[i].loadCase.slsUls + loadCaseResult[i].loadCase.wind.type + loadCaseResult[i].loadCase.wind.direction}`,
                )}
              </p>
              <Icon
                iconName={getIconByWindDirection(
                  loadCaseResult[i].loadCase.wind.direction,
                )}
                className={styles.loadCaseIcon}
              />
            </div>
          </TooltipHost>
        </td>

        {renderLoadCase(
          loadCaseResult[i].loadCaseResultValues,
          loadCaseResult[i].mastValidationControls,
          loadCaseResult[i].foundationValidationControls,
          loadCaseResult[i].footplateValidationControls,
        )}
      </tr>,
    );
  }
  return result;
};

const getIconByWindDirection = (windDirection: string): string => {
  const windDirectionIcons: Record<string, string> = {
    "-": "Cancel",
    ">": "Forward",
    "<": "Back",
    "^": "Up",
    v: "Down",
  };
  return windDirectionIcons[windDirection];
};

const renderLoadCase = (
  loadCaseResultValues: LoadCaseResultValueDto[],
  mastValidationControls: MastValidationControlDto[],
  foundationValidationControls: FoundationValidationControlDto,
  footplateValidationControls: boolean | null | undefined,
): React.ReactNode => {
  const results: React.ReactNode[] = [];

  for (let i = 0; i < loadCaseResultValues.length; i++) {
    const maxValue = loadCaseResultValues[i].isMaxValue
      ? styles.isMaxValue
      : "";
    if (loadCaseResultValues[i].hasValidationControl) {
      const className = loadCaseResultValues[i].isValid
        ? styles.greenBox
        : styles.redBox;

      results.push(
        <td
          key={`${i}-${loadCaseResultValues[i].value}`}
          className={`${className} ${maxValue}`}
          title={loadCaseResultValues[i].value?.toString()}>
          {(
            Math.round(
              ((loadCaseResultValues[i].value ?? 0) + Number.EPSILON) * 1000,
            ) / 1000
          )?.toFixed(3)}
        </td>,
      );
    } else {
      results.push(
        <td
          className={maxValue}
          key={`${i}-${loadCaseResultValues[i].value}`}
          title={loadCaseResultValues[i].value?.toString()}>
          {(
            Math.round(
              ((loadCaseResultValues[i].value ?? 0) + Number.EPSILON) * 1000,
            ) / 1000
          )?.toFixed(3)}
        </td>,
      );
    }
  }

  for (let i = 0; i < mastValidationControls.length; i++) {
    addCheckMarkCellToRow(
      results,
      i,
      mastValidationControls[i].value,
      mastValidationControls[i].isVisible as boolean,
    );
  }

  addCheckMarkCellToRow(
    results,
    0,
    foundationValidationControls?.isFoundationValid ?? false,
    foundationValidationControls?.isFoundationValid !== null,
  );

  addCheckMarkCellToRow(
    results,
    0,
    footplateValidationControls ?? false,
    footplateValidationControls ?? false,
  );

  return results;
};

// TODO Temporary code to generate add 'CheckMark' table cells to rows. Should be removed after refactoring to use IsaacTable
const addCheckMarkCellToRow = (
  result: React.ReactNode[],
  cellNumber: number,
  isValid: boolean,
  shouldAddCell: boolean,
): void => {
  if (shouldAddCell) {
    result.push(generateCheckMarkCell(cellNumber, isValid));
  }
};

const generateCheckMarkCell = (
  cellNumber: number,
  isValid: boolean,
): React.ReactNode => {
  const className = isValid ? styles.greenBox : styles.redBox;
  const value = isValid ? (
    <Icon iconName="CheckMark" />
  ) : (
    <Icon iconName="Error" />
  );

  return (
    <td key={`${uuidv4()}-${cellNumber}`} className={className}>
      {value}
    </td>
  );
};

